import { useCallback, useState } from "react";

import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import { Close as CloseIcon } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import {
  DataGrid,
  gridRowSelectionIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";

import { useOutletApi } from "@/features/authentication";
import { SalesReturnStatusEnum, StatusEnum } from "@/features/delivery-orders";
import { formatDate, formatNumber, getErrorMessage } from "@/features/shared";
import {
  CustomAsyncAutocomplete,
  ProductVariantNameCell,
  useSnackbar,
} from "@/features/ui";
import { ReturnableTypeEnum } from "../../returnable-type";
import { resolveInitialStockInMode } from "../../sales-return-rules";

export const SelectDeliveryOrderProductVariantSection = () => {
  const { control, setValue } = useFormContext();
  const { showSnackbar } = useSnackbar();
  const apiRef = useGridApiRef();
  const [deliveryOrder, setDeliveryOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [productVariants, setProductVariants] = useState([]);
  const [loadingProductVariants, setLoadingProductVariants] = useState(false);
  const [
    selectedDeliveryOrderProductVariants,
    setSelectedDeliveryOrderProductVariants,
  ] = useState([]);

  const { append } = useFieldArray({
    control,
    name: "returnableProductVariants",
  });

  const [buyer, seller, returnableProductVariants, returnableId, stockInMode] =
    useWatch({
      control,
      name: [
        "buyer",
        "seller",
        "returnableProductVariants",
        "returnableId",
        "stockInMode",
      ],
      exact: true,
    });

  const outletApi = useOutletApi(seller?.id);

  // Disable if buyer not selected OR if items exist from different source
  const disableBrowseDeliveryOrder =
    !buyer || (returnableProductVariants?.length > 0 && returnableId !== null);

  const fetchDeliveryOrders = useCallback(
    async (searchValue, { page, size } = {}) => {
      if (!buyer || !seller) return [];

      const returnableStatuses = [
        SalesReturnStatusEnum.NONE,
        SalesReturnStatusEnum.PARTIALLY_RETURNED,
      ];

      const invoiceStatuses = [
        StatusEnum.DELIVERED,
        StatusEnum.PARTIALLY_INVOICED,
      ].join(",");

      const {
        data: { data },
      } = await outletApi.get("delivery-orders", {
        params: {
          buyerableId: buyer?.id,
          referenceNumber: searchValue || null,
          returnStatuses: returnableStatuses,
          status: invoiceStatuses,
          excludeInvoiced: true,
          page,
          size,
        },
      });
      return data;
    },
    [seller, buyer, outletApi]
  );

  const fetchDeliveryOrderProductVariants = useCallback(
    async (referenceNumber) => {
      if (!referenceNumber || !seller) return;

      setLoadingProductVariants(true);

      try {
        const {
          data: { data },
        } = await outletApi.get(
          `delivery-orders/${referenceNumber}/delivery-order-product-variants`,
          {
            params: {
              withSalesOrderProductVariant: true,
              excludeFullyReturned: true,
            },
          }
        );

        // Adjust remaining quantity based on existing return items
        const adjustedProductVariants = data
          .map((deliveryOrderProductVariant) => {
            const totalReturnedQuantityInCurrentForm = returnableProductVariants
              .filter(
                (returnProductVariant) =>
                  returnProductVariant.returnableProductVariantId ===
                  deliveryOrderProductVariant.id
              )
              .reduce((total, variant) => total + variant.quantity, 0);

            return {
              ...deliveryOrderProductVariant,
              adjustedRemainingQuantity:
                deliveryOrderProductVariant.quantity -
                deliveryOrderProductVariant.returnedQuantity -
                totalReturnedQuantityInCurrentForm,
            };
          })
          .filter((variant) => variant.adjustedRemainingQuantity > 0);

        setProductVariants(adjustedProductVariants);
      } catch (error) {
        showSnackbar(
          getErrorMessage(error, "Gagal memuat rincian surat jalan"),
          "error"
        );
      } finally {
        setLoadingProductVariants(false);
      }
    },
    [seller, returnableProductVariants, outletApi, showSnackbar]
  );

  const handleDeliveryOrderChange = (newValue) => {
    setDeliveryOrder(newValue);
    if (newValue) {
      fetchDeliveryOrderProductVariants(newValue.referenceNumber);
      setDialogOpen(true);
    } else {
      setProductVariants([]);
    }
    setSelectedDeliveryOrderProductVariants([]);
  };

  const columns = [
    {
      field: "name",
      headerName: "Nama Barang",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} />
      ),
    },
    {
      field: "adjustedRemainingQuantity",
      headerName: "Kuantitas",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "unitOfMeasurement",
      headerName: "Satuan",
      valueGetter: (_, row) => row.product.unitOfMeasurement.name,
    },
  ];

  const submitSelection = () => {
    // Check if there's already a returnable source set
    if (returnableId && returnableId !== deliveryOrder.id) {
      showSnackbar(
        "Anda hanya dapat memilih produk dari satu sumber dokumen. Hapus produk yang ada terlebih dahulu.",
        "error"
      );
      return;
    }

    // Set the returnable source if not already set
    if (!returnableId) {
      setValue("returnableId", deliveryOrder.id);
      setValue("returnableType", ReturnableTypeEnum.DELIVERY_ORDER);
    }

    selectedDeliveryOrderProductVariants.forEach(
      ({
        id: deliveryOrderProductVariantId,
        name,
        sku,
        adjustedRemainingQuantity,
        product: {
          unitOfMeasurement: { name: unitOfMeasurement },
        },
        salesOrderProductVariant,
      }) => {
        const alreadyExists = returnableProductVariants.some(
          (variant) =>
            variant.returnableProductVariantId === deliveryOrderProductVariantId
        );

        if (!alreadyExists) {
          // Determine initial stockInMode based on main stockInMode setting
          // For PARTIAL mode, default to FULL

          append({
            id: ulid(),
            returnableProductVariantId: deliveryOrderProductVariantId,
            warehouseId: deliveryOrder.warehouseId,
            name,
            sku,
            quantity: adjustedRemainingQuantity,
            maxQuantity: adjustedRemainingQuantity,
            unitOfMeasurement,
            price: salesOrderProductVariant?.pricePerUnit ?? 0,
            discount: 0,
            note: null,
            stockInMode: resolveInitialStockInMode(stockInMode),
          });
        }
      }
    );

    // Close dialog
    setDialogOpen(false);
    setProductVariants([]);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setProductVariants([]);
  };

  return (
    <>
      <CustomAsyncAutocomplete
        value={deliveryOrder}
        onChange={handleDeliveryOrderChange}
        disabled={disableBrowseDeliveryOrder}
        fetchOptions={fetchDeliveryOrders}
        getOptionLabel={(option) => option.referenceNumber}
        isOptionEqualToValue={(option, selectedOption) =>
          option.id === selectedOption.id
        }
        renderOption={(props, option) => {
          const { id, referenceNumber, transactionDate } = option;
          return (
            <li {...props} key={id}>
              <div>
                <div>{referenceNumber}</div>
                <div style={{ fontSize: "0.75rem" }}>
                  {formatDate(transactionDate)}
                </div>
              </div>
            </li>
          );
        }}
        TextFieldProps={{
          helperText: disableBrowseDeliveryOrder
            ? !buyer
              ? "Pilih Pelanggan terlebih dahulu"
              : "Tidak dapat mengubah sumber dokumen setelah menambah produk"
            : undefined,
          InputProps: {
            placeholder: "Cari No. Referensi Surat Jalan",
          },
        }}
      />

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Rincian Barang
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DataGrid
            getRowHeight={() => "auto"}
            apiRef={apiRef}
            columns={columns}
            rows={productVariants}
            loading={loadingProductVariants}
            checkboxSelection
            onRowSelectionModelChange={() => {
              const selectedModels = Array.from(
                gridRowSelectionIdsSelector(apiRef.current.state).values()
              );
              setSelectedDeliveryOrderProductVariants(selectedModels);
            }}
            sx={{ minHeight: 400 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            fullWidth
            variant="contained"
            disabled={selectedDeliveryOrderProductVariants.length === 0}
            onClick={submitSelection}
          >
            Lanjut
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
